import React, {useMemo, useState} from 'react';
import styled, {createGlobalStyle} from 'styled-components';
import {DropdownList, Icon, Text, TextField} from '@forecast-it/design-system';
import {useIntl, type IntlShape} from 'react-intl';
import {chatFont, D, KV_ROW_MIN_HEIGHT, kvKeyType, kvValueType, skyTokens} from './design-tokens';
import {KeyValueField} from './KeyValueField';
import {validateField} from './fieldValidation';
import {useFieldOptions} from './useFieldOptions';
import type {FieldDescriptor, FieldOption} from './actionCardTypes';

// ── Styled layout — mirrors Figma "Action Box/Write/Dropdown" key-value rows ──

const Table = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
`;

// Per Figma "Action Box / Write / Dropdown" — every row uses the SAME fixed
// height regardless of content type. min-height (not height) so a row can still
// grow when wrapped text exceeds it, but the empty / closed / em-dash / link /
// input / date-range states all line up. Shared with KeyValueTable.
const Row = styled.div<{$table?: boolean}>`
	display: flex;
	gap: 8px;
	align-items: center;
	min-height: ${KV_ROW_MIN_HEIGHT}px;
	padding: 0;
	${({$table}) =>
		$table
			? `border-bottom: 1px solid ${skyTokens.color.border.decorative};
		   &:last-child { border-bottom: none; }`
			: ''}
`;

const Key = styled.div`
	min-width: 100px;
	${kvKeyType};
`;

const Value = styled.div`
	flex: 1 0 0;
	min-width: 0;
	${kvValueType};
	display: flex;
	align-items: center;
	gap: 4px;
	overflow: hidden;
`;

// Wraps the in-row input so it can flex within the fixed-height Row. No vertical
// padding — the Row's centred alignment positions the input visually.
const FieldWrapper = styled.div`
	flex: 1;
	min-width: 0;
	width: 100%;
	word-break: break-word;
`;

// Validation message lives OUTSIDE the Row so a mandatory field with an error
// doesn't make its row taller than the others. Sits as a sibling under the Row,
// indented past the Key column so it reads as "this row's error".
const ErrorRow = styled.div`
	font-family: ${chatFont};
	font-size: 12px;
	line-height: 16px;
	color: ${D.errorText};
	padding: 2px 0 6px 108px; // 100px key column + 8px row gap
`;

// Hover-only icon trigger — borderless, fades in on Row hover (Figma "hovering over a
// field, an icon appears" sticky note). Icon varies by field type — calendar for
// date/daterange, edit pencil otherwise.
const HoverIcon = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 0;
	border: none;
	background: none;
	cursor: pointer;
	color: ${D.v2.iconInactive};
	opacity: 0;
	transition: opacity 0.15s ease;
	flex-shrink: 0;

	${Row}:hover & {
		opacity: 1;
	}

	&:focus-visible {
		opacity: 1;
		outline: 2px solid ${D.borderFocus};
		border-radius: 2px;
	}
`;

// Radix's DropdownList.Trigger renders the child as a <button>, so the trigger
// can't itself contain a DS <Link> (interactive-in-interactive is invalid HTML
// and breaks accessibility). ChooseTriggerButton styles the bare button to
// match the Figma "+ Choose <Label>" CTA visually — blue weight-500 text, plus
// icon inheriting currentColor — without nesting another <a>.
const ChooseTriggerButton = styled.button`
	all: unset;
	box-sizing: border-box;
	display: inline-flex;
	align-items: center;
	gap: 4px;
	cursor: pointer;
	color: ${D.v2.accent};
	font-family: ${chatFont};
	font-size: 14px;
	font-weight: 500;
	line-height: 20px;
	letter-spacing: 0.15px;
	svg path {
		fill: currentColor;
	}
	&:hover {
		text-decoration: underline;
	}
	&:focus-visible {
		outline: 2px solid ${D.borderFocus};
		border-radius: 2px;
	}
`;

// Trigger for already-filled select / multiselect rows. Same reset chrome but
// inherits the parent <Value> cell's body-text style — clicking the value text
// re-opens the dropdown.
const ValueTriggerButton = styled.button`
	all: unset;
	box-sizing: border-box;
	display: inline-flex;
	align-items: center;
	gap: 4px;
	cursor: pointer;
	color: inherit;
	font: inherit;
	text-align: left;
	&:focus-visible {
		outline: 2px solid ${D.borderFocus};
		border-radius: 2px;
	}
`;

// Decorative pencil glyph for an already-filled select / multiselect trigger —
// fades in on Row hover, matching the Figma "hovering over a field, an icon
// appears" behaviour. Not its own button (nesting a button inside
// ValueTriggerButton would be invalid HTML): the whole trigger is already
// clickable, so this is purely a visual affordance riding along.
const TriggerHoverIcon = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	color: ${D.v2.iconInactive};
	opacity: 0;
	transition: opacity 0.15s ease;
	flex-shrink: 0;

	${Row}:hover & {
		opacity: 1;
	}
`;

const SearchRow = styled.div`
	padding: 8px 16px;
	border-bottom: 1px solid ${D.assistantBubbleBorder};
	margin-bottom: 4px;
`;

// Show a search input inside the dropdown content once the option count crosses
// this threshold — small lists don't need filtering and the extra input wastes vertical space.
const SEARCH_THRESHOLD = 7;

// DS `DropdownList.Item` enforces `min-height: 44px` via `selectableItemCss`, and
// DS's `Content` component doesn't forward `className`, so we can't reach the
// portal-rendered items through the React tree. We can't import Radix directly
// either — DS bundles its Radix via CJS, our ESM import would resolve a second
// copy and the `MenuItem must be used within Menu` context check fails.
//
// Solution: a scoped `createGlobalStyle` that targets the menu items inside an
// element marked `data-compact-dropdown`. We thread that marker onto items via
// a `data-cy` style trick on `Item` — but `Item` only forwards specific props,
// so instead we mount the override at the document root and scope it via the
// chooseLabel-derived `aria-labelledby`. Concretely: target Radix's own
// `role="menu"` container that immediately follows our trigger.
//
// In practice every chat-drawer single-select DropdownList (Role, Phase, ...)
// wants the same compact rows, so a body-level rule keyed on the menu items
// themselves is acceptable — we still constrain it to portal-rendered menus
// (`[role="menu"] > ...`) so it can't leak into unrelated DS surfaces that
// don't use `role="menu"`. `menuitemcheckbox` (multiselect rows) is
// deliberately NOT included here — those keep the DS's native 44px row height
// rather than the single-select compact one.
const CompactDropdownGlobalStyle = createGlobalStyle`
	[role='menu'] [role='menuitem'],
	[role='menu'] [role='menuitemradio'] {
		min-height: 32px;
		padding: 4px 16px;
		font-family: 'ABC Whyte', Inter, sans-serif;
		font-weight: 350;
		font-size: 16px;
		line-height: 24px;
		letter-spacing: 0.15px;
		color: #232334;
		&:hover,
		&[data-highlighted] {
			background: #e5f4ff;
		}
	}

	/* Keyline separating "Select all" from the individual options below it —
	   [data-cy] is the only prop CheckboxItem forwards to the DOM node, so it
	   doubles as our styling hook here, same trick as the compact-row rule above. */
	[role='menu'] [data-cy='multiselect-select-all'] {
		border-bottom: 1px solid ${D.assistantBubbleBorder};
	}
`;

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * A field row renders open (input immediately visible) when:
 *   1. The field is a "free-text-like" type (text or number), AND
 *   2. It is required (mandatory), OR
 *   3. The user has explicitly opened it via the hover icon (tracked in openKeys state).
 */
const isTextLike = (type: FieldDescriptor['type']): boolean => type === 'text' || type === 'number' || type === 'duration';

/** True for null/undefined/empty-string, empty arrays, and empty {start,end} ranges. */
const isEmptyValue = (value: unknown): boolean => {
	if (value === null || value === undefined || value === '') return true;
	if (Array.isArray(value)) return value.length === 0;
	if (typeof value === 'object' && value !== null && 'start' in (value as object)) {
		const {start, end} = value as {start?: string | null; end?: string | null};
		return !start && !end;
	}
	return false;
};

/** Date / daterange fields use the calendar icon as their hover trigger. */
const usesCalendarIcon = (type: FieldDescriptor['type']): boolean => type === 'date' || type === 'daterange';

/** Empty select / multiselect fields surface a "+ Choose <Label>" Link CTA instead of an em-dash. */
const usesChooseLink = (type: FieldDescriptor['type']): boolean => type === 'select' || type === 'multiselect';

/** "PROJECT NAME" → "Project Name", "ROLE" → "Role", "assignee" → "Assignee". */
const titleCase = (s: string): string => s.toLocaleLowerCase().replace(/(?:^|\s)\p{L}/gu, c => c.toLocaleUpperCase());

const VALIDATION_PREFIX = 'forecast_agent.action_card.validation';

// Reused verbatim from the live Create Project modal's own "already in use"
// check (new_project_modal_v2.tsx's onCustomProjectIdBlur) — ActionCard.tsx
// sets this as a project_id field's `fieldOverrideErrors` entry once its live
// ValidateProjectId check comes back negative. Exported so ActionCard.tsx
// reads the same constant rather than duplicating the literal string.
export const PROJECT_ID_ALREADY_IN_USE_KEY = 'new_project_modal.project_id_error.already_in_use';

// Set by ActionCard on a time entry's billable time when it is more than the duration, which the
// server refuses.
export const BILLABLE_OVER_DURATION_KEY = `${VALIDATION_PREFIX}.billable_over_duration`;

// Human-readable fallbacks for non-required validation keys — keeps Storybook / dev
// builds from showing bare ids when defaultMessages haven't been wired through
// translations:update yet. Per Figma "Required fields auto-open": empty-required
// reads "Please enter a <field name>" with the label lower-cased, NOT the raw id.
const VALIDATION_FALLBACKS: Record<string, string> = {
	[`${VALIDATION_PREFIX}.min`]: 'Value is too low',
	[`${VALIDATION_PREFIX}.max`]: 'Value is too high',
	[`${VALIDATION_PREFIX}.max_length`]: 'Value is too long',
	[`${VALIDATION_PREFIX}.pattern`]: 'Value does not match the required pattern',
	[`${VALIDATION_PREFIX}.min_date`]: 'Date is too early',
	[`${VALIDATION_PREFIX}.max_date`]: 'Date is too late',
	[`${VALIDATION_PREFIX}.date_order`]: 'End date must be after start date',
	[PROJECT_ID_ALREADY_IN_USE_KEY]: 'Project ID already in use',
	[BILLABLE_OVER_DURATION_KEY]: 'Billable time cannot be more than the duration',
};

const formatErrorMessage = (errorKey: string, field: FieldDescriptor, formatMessage: IntlShape['formatMessage']): string => {
	if (errorKey === `${VALIDATION_PREFIX}.required`) {
		return formatMessage(
			{id: `${VALIDATION_PREFIX}.required_named`, defaultMessage: 'Please enter a {label}'},
			{label: field.label.toLocaleLowerCase()}
		);
	}
	return formatMessage({id: errorKey, defaultMessage: VALIDATION_FALLBACKS[errorKey] ?? errorKey});
};

// ── SelectField ──────────────────────────────────────────────────────────────
//
// Select / multi-select trigger anchored to the "+ Choose <Label>" link (per
// Figma "Action Box / Task / New"). Replaces the earlier ComboBox-on-open flow,
// which hid the chooseLink behind a text input. With DropdownList:
//   - Trigger = chooseLink (empty) or value display (filled). Stays visible.
//   - Content = option list. ButtonItem for single, CheckboxItem for multi.
//   - When option count > SEARCH_THRESHOLD a TextField appears at the top of
//     Content and filters the list in-place (DropdownList has no built-in
//     filter — Radix only provides keyboard type-ahead, which doesn't show the
//     query). Filtering is local; commit semantics are unchanged.
type SelectFieldProps = {
	field: FieldDescriptor;
	value: unknown;
	onCommit: (next: unknown) => void;
	chooseLabel: string;
};

const SelectField = ({field, value, onCommit, chooseLabel}: SelectFieldProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const {options} = useFieldOptions(field);
	const isMulti = field.type === 'multiselect';
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState('');

	// Selected id(s) normalised into an array — even for single select — so the
	// trigger label and CheckboxItem `checked` reading is one code path.
	const selectedIds = useMemo<string[]>(() => {
		if (isMulti) return Array.isArray(value) ? (value as string[]) : [];
		return typeof value === 'string' && value !== '' ? [value] : [];
	}, [value, isMulti]);

	const showSearch = options.length > SEARCH_THRESHOLD;
	const filteredOptions = useMemo<FieldOption[]>(() => {
		if (!showSearch || query === '') return options;
		const needle = query.toLocaleLowerCase();
		return options.filter(o => o.label.toLocaleLowerCase().includes(needle));
	}, [options, query, showSearch]);

	const isEmpty = selectedIds.length === 0;
	// A multiselect with every current option selected collapses to "All"
	// rather than spelling out the full list — matches the design's default
	// (untouched) state for a fixed checklist field.
	const isAllSelected = isMulti && options.length > 0 && selectedIds.length === options.length;
	const triggerLabel: string | null = isEmpty
		? null
		: isAllSelected
		? formatMessage({id: 'common.all', defaultMessage: 'All'})
		: isMulti
		? selectedIds.map(id => options.find(o => o.id === id)?.label ?? id).join(', ')
		: options.find(o => o.id === selectedIds[0])?.label ?? selectedIds[0];

	const handleSelect = (id: string): void => {
		if (isMulti) {
			const next = selectedIds.includes(id) ? selectedIds.filter(s => s !== id) : [...selectedIds, id];
			onCommit(next);
			// Keep dropdown open so the user can keep picking.
		} else {
			onCommit(id);
			setOpen(false);
			setQuery('');
		}
	};

	// "Select all" always toggles the FULL option set, never the filtered
	// subset — scoping it to filtered results would make repeated toggles
	// while filtering silently drop selections outside the current filter.
	// Because of that, the row is hidden while a search query is narrowing the
	// list (query !== ''): showing it during search would let its checked
	// state (computed against the full set) visually disagree with what's on
	// screen, e.g. reading "unchecked" even though every currently visible row
	// is checked. Clearing the query (or not searching at all) always makes
	// visible === full, so the control is unambiguous whenever it's shown.
	const showSelectAll = isMulti && options.length > 0 && query === '';
	const allSelected = showSelectAll && options.every(o => selectedIds.includes(o.id));
	const handleSelectAll = (): void => {
		onCommit(allSelected ? [] : options.map(o => o.id));
	};

	return (
		<DropdownList
			open={open}
			onOpenChange={next => {
				setOpen(next);
				if (!next) setQuery('');
			}}
		>
			<DropdownList.Trigger>
				{isEmpty ? (
					<ChooseTriggerButton type="button" aria-label={chooseLabel}>
						<Icon icon="plus" size="s" />
						{chooseLabel}
					</ChooseTriggerButton>
				) : (
					<ValueTriggerButton type="button" aria-label={`${chooseLabel}: ${triggerLabel}`}>
						<Text size="2">{triggerLabel}</Text>
						<TriggerHoverIcon>
							<Icon icon="edit" size="s" />
						</TriggerHoverIcon>
					</ValueTriggerButton>
				)}
			</DropdownList.Trigger>
			<DropdownList.Content side="bottom" align="start" useTriggerWidth={false}>
				{showSearch ? (
					<SearchRow>
						<TextField
							value={query}
							onValueChange={setQuery}
							size={TextField.SIZE.SMALL}
							width={TextField.WIDTH.FLEX}
							aria-label={chooseLabel}
						/>
					</SearchRow>
				) : null}
				{showSelectAll ? (
					<DropdownList.CheckboxItem
						checked={allSelected}
						onChange={handleSelectAll}
						data-cy="multiselect-select-all"
					>
						{formatMessage({id: 'forecast_agent.action_card.select_all', defaultMessage: 'Select all'})}
					</DropdownList.CheckboxItem>
				) : null}
				{filteredOptions.map(o =>
					isMulti ? (
						<DropdownList.CheckboxItem
							key={o.id}
							checked={selectedIds.includes(o.id)}
							onChange={() => handleSelect(o.id)}
						>
							{o.label}
						</DropdownList.CheckboxItem>
					) : (
						// `Item` (not ButtonItem) keeps the body/1 typography (ABC Whyte 16/24/350)
						// and applies the row-level hover background. CompactDropdownGlobalStyle
						// brings the row min-height down to 32px to match Figma.
						<DropdownList.Item key={o.id} onClick={() => handleSelect(o.id)}>
							{o.label}
						</DropdownList.Item>
					)
				)}
			</DropdownList.Content>
		</DropdownList>
	);
};

// ── Component ─────────────────────────────────────────────────────────────────

type Props = {
	fields: FieldDescriptor[];
	edits: Record<string, unknown>;
	onEdit: (key: string, value: unknown, typed?: boolean) => void;
	readOnly?: boolean;
	// Async validation results keyed by field key (e.g. a live "already in
	// use" check that can't be expressed as a synchronous `constraints` rule
	// in validateField) — merged into the row's error display and, via the
	// caller's Confirm-gating, blocks confirming while set.
	fieldOverrideErrors?: Record<string, string>;
};

export const KeyValueForm = ({fields, edits, onEdit, readOnly = false, fieldOverrideErrors}: Props): JSX.Element | null => {
	const {formatMessage} = useIntl();
	const [openKeys, setOpenKeys] = useState<Set<string>>(new Set());

	if (fields.length === 0) return null;

	// Table-mode bottom borders only kick in for >3 fields (matches Figma — small cards
	// look better visually grouped without internal dividers; >3 cards benefit from
	// the row separators).
	const useTableLayout = fields.length > 3;

	const openKey = (key: string) => {
		setOpenKeys(prev => {
			const next = new Set(prev);
			next.add(key);
			return next;
		});
	};

	return (
		<Table>
			<CompactDropdownGlobalStyle />
			{fields.map(field => {
				const currentValue = edits[field.key];
				// A synchronous constraint violation takes precedence, but an async
				// result (e.g. "already in use") still blocks confirming and shows a
				// message even once the field satisfies every static constraint.
				const errorKey = validateField(field, currentValue) ?? fieldOverrideErrors?.[field.key] ?? null;

				const isEmpty = isEmptyValue(currentValue);
				const isMandatoryTextLike = field.editable && isTextLike(field.type) && field.required === true;
				const isUserOpened = openKeys.has(field.key);
				// Select / multiselect rows render a DropdownList that manages its own
				// open state — they never participate in the row-level open/closed
				// transition, so the SelectField branch sees `isOpen === false` and
				// always renders its trigger (choose-link or value display).
				const usesSelectField = !readOnly && field.editable && usesChooseLink(field.type);
				// Date / daterange rows render the DS picker, which already exposes a
				// calendar trigger. The row-level HoverIcon would be a redundant second
				// affordance (user clicks HoverIcon → row "opens" → THEN they see the
				// picker's calendar icon → second click to open the calendar). Skip the
				// openKey/HoverIcon transition for these and let the picker manage itself.
				const usesPickerField = !readOnly && field.editable && (field.type === 'date' || field.type === 'daterange');
				const usesSelfManagedTrigger = usesSelectField || usesPickerField;
				const isOpen = !readOnly && field.editable && !usesSelfManagedTrigger && (isMandatoryTextLike || isUserOpened);

				// Empty + editable + not handled by a self-managed trigger → em-dash placeholder.
				const showEmptyMark = !readOnly && field.editable && !isOpen && isEmpty && !usesSelfManagedTrigger;
				// Hover icon visible whenever the row is closed + editable + not readonly-typed AND
				// no self-managed trigger is already serving as the open-affordance. Icon is the
				// edit pencil — picker fields show their own calendar trigger directly.
				const showHoverIcon =
					!readOnly && field.editable && field.type !== 'readonly' && !isOpen && !usesSelfManagedTrigger;

				const editAriaLabel = formatMessage(
					{id: 'forecast_agent.action_card.edit_field', defaultMessage: 'Edit {label}'},
					{label: field.label}
				);
				const chooseLabel = formatMessage(
					{id: 'forecast_agent.action_card.choose_field', defaultMessage: 'Choose {label}'},
					{label: titleCase(field.label)}
				);

				return (
					<React.Fragment key={field.key}>
						<Row $table={useTableLayout}>
							<Key>{field.label}</Key>
							<Value>
								{usesSelectField ? (
									<SelectField
										field={field}
										value={currentValue}
										onCommit={value => onEdit(field.key, value)}
										chooseLabel={chooseLabel}
									/>
								) : showEmptyMark ? (
									// size="2" → DS sky/sys/font/body/2 (14px / 20px / 0.15px / weight 350). DS Text
									// applies its own font token internally and ignores the parent Value cell's
									// font-family/font-weight, so the size has to come from the DS prop.
									<Text size="2">—</Text>
								) : (
									<FieldWrapper>
										<KeyValueField
											descriptor={field}
											value={currentValue}
											onCommit={(...commit: [unknown, boolean?]) => onEdit(field.key, ...commit)}
											errorKey={errorKey ?? undefined}
											// Picker fields always render their own editable trigger; for
											// everything else, readOnly tracks the row open state so closed
											// rows still show their value as plain text.
											readOnly={usesPickerField ? readOnly : !isOpen}
										/>
									</FieldWrapper>
								)}
								{showHoverIcon && (
									<HoverIcon type="button" aria-label={editAriaLabel} onClick={() => openKey(field.key)}>
										<Icon icon={usesCalendarIcon(field.type) ? 'calendar' : 'edit'} size="s" />
									</HoverIcon>
								)}
							</Value>
						</Row>
						{/* Show the validation message for an open text/number row OR for a
						    self-managed trigger (select / date / daterange), which never "opens"
						    but can still be invalid (e.g. a required-but-empty dropdown). Without
						    the self-managed case a required dropdown/date disables Confirm with no
						    visible reason. An error the card sets from other fields shows on a
						    closed row too, since the reader may not have touched this one. */}
						{(isOpen || usesSelfManagedTrigger || fieldOverrideErrors?.[field.key] != null) && errorKey ? (
							<ErrorRow>{formatErrorMessage(errorKey, field, formatMessage)}</ErrorRow>
						) : null}
					</React.Fragment>
				);
			})}
		</Table>
	);
};
